import { lazy } from 'react';
import i18next from 'i18next';
import { AssessmentsIcon } from '@shared/ui/icons';

const AssessmentReportPage = lazy(() => import('@pages/assessment-report'));
const Assessments = lazy(() => import('@pages/assessments'));

const ASSESSMENT_ROUTE_LIST_DS = {
  path: '/:currentAccountUuid/accounts/:accountUuid/assessments',
  exact: true,
  additional: {
    component: Assessments,
    protected: true,
    title: i18next.t('titles:assessments'),
    icon: AssessmentsIcon,
    breadcrumb: {
      // Mirror the current-account variant — root crumb labelled
      // "Assessments" with no Accounts ancestor. Even though the
      // URL is nested under `/accounts/:au/`, the user reaches
      // this page through the sidebar (or a tab inside the
      // account), not through the Accounts list — so showing
      // `Accounts → Assessments` on deep-link reload diverges
      // from normal navigation. Click-through still returns to
      // the same DS URL because routing keys off `:accountUuid`.
      isRoot: true,
      label: ({ state }) => state.i18n?.en?.titles?.assessments || 'Assessments',
    },
  },
};

const ASSESSMENT_ROUTE_REPORT_DS = {
  path: '/:currentAccountUuid/accounts/:accountUuid/patients/:patientUuid/assessments/:assessmentUuid/:status/report',
  exact: true,
  parentRoute: ASSESSMENT_ROUTE_LIST_DS,
  additional: {
    component: AssessmentReportPage,
    protected: true,
    title: i18next.t('titles:reports'),
    breadcrumb: {
      isRoot: false,
      // Chain to the Assessments list, not the patient dashboard.
      // The patient-dashboard parent path produced an extra
      // "Patient name" crumb (with an async-resolving skeleton) on
      // deep-link reload that never appears in normal navigation —
      // user arrived at the report from the assessments list, not
      // the patient page.
      parentPath: '/:currentAccountUuid/accounts/:accountUuid/assessments',
      label: ({ state }) => state.i18n?.en?.titles?.report || 'Assessment',
      replaceSibling: true,
    },
  },
};

const ASSESSMENT_ROUTE_LIST = {
  path: '/:currentAccountUuid/assessments',
  exact: true,
  additional: {
    component: Assessments,
    protected: true,
    title: i18next.t('titles:assessments'),
    icon: AssessmentsIcon,
    breadcrumb: {
      isRoot: true,
      label: ({ state }) => state.i18n?.en?.titles?.assessments || 'Assessments',
    },
  },
};

const ASSESSMENT_ROUTE_REPORT = {
  path: '/:currentAccountUuid/patients/:patientUuid/assessments/:assessmentUuid/:status/report',
  exact: true,
  parentRoute: ASSESSMENT_ROUTE_LIST,
  additional: {
    component: AssessmentReportPage,
    protected: true,
    title: i18next.t('titles:reports'),
    breadcrumb: {
      isRoot: false,
      // Mirror the DS variant: chain to the Assessments list, not
      // the patient dashboard. See ASSESSMENT_ROUTE_REPORT_DS.
      parentPath: '/:currentAccountUuid/assessments',
      label: ({ state }) => state.i18n?.en?.titles?.report || 'Assessment',
      replaceSibling: true,
    },
  },
};

export const ASSESSMENT_ROUTES = {
  // ------- For child accounts ------
  listDS: ASSESSMENT_ROUTE_LIST_DS,
  reportDS: ASSESSMENT_ROUTE_REPORT_DS,

  // ------- For current accounts ------
  list: ASSESSMENT_ROUTE_LIST,
  report: ASSESSMENT_ROUTE_REPORT,
};
